import { Button, Fade, Form, Image, Stack } from "react-bootstrap";
import { useAuth } from "../api/auth/store";
import Loading from "../components/Loading";
import { createRef, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import Topbar from "../components/Topbar"
import useUXStore from "../api/ux/store"
import { getErrorMessage } from "../utils/errors"
import { redirectToUrl } from "../utils/functions";
import { getLastCompany, setLastCompany } from "../utils/lastCompany";
import {
    CLIENT_ID, ERR_ACC_BLOCKED, ERR_ACCOUNT_FAILED,
    ERR_AUTH_INVALID, ERR_AWAITING_RESET, ERR_COOKIE,
    ERR_DEVICE_ADD, ERR_DEVICE_APPROVAL_PENDING, ERR_DEVICE_APPROVAL_REJECTED,
    ERR_DEVICE_NOT_FOUND, ERR_DEVICE_USER_MISMATCH, ERR_INVALID_PASS,
    ERR_INVALID_PIN, ERR_IP_NOT_WHITELISTED, ERR_RATE_LIMIT, ERR_SESSION_EXPIRED,
    ERR_SESSION_INVALID, HTTP_OK, USER_NOT_FOUND, ERR_MFA_REQUIRED
} from "../constants";
import Label from "../components/Label";
import { AuthData, AuthResponse, CompanyData } from "../api/auth/actions";
import CompanySelect from "../components/CompanySelector";
import { ReactComponent as CheckIcon } from "../svgs/check_badge.svg";
import { ReactComponent as LockIcon } from "../svgs/lock.svg";
import ServerError from "../containers/Auth/ServerError";
import DeviceVerification from "../containers/Auth/DeviceVerification"; // Import the new component
import Logo from "../assets/images/logo.png";
import Reauth from "../containers/Auth/Reauth";
import LoginMfaChallenge from "../containers/Auth/LoginMfaChallenge";

interface AllowPageProps {
    iconlink: string;
    app: string;
    code: string;
    redirect: string;
    company: CompanyData;
    onContinue?: () => void;
}

const AllowPage: React.FC<AllowPageProps> = ({ iconlink, app, code, redirect, company, onContinue }) => {
    const [countdown, setCountdown] = useState(2);
    
    const handleContinue = () => {
        if (onContinue) {
            onContinue();
        } else {
            redirectToUrl(redirect, code, company.id);
        }
    };

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleContinue();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    return (
        <div className="center invite-container">
            <Stack gap={3} className="text-center">
                <Image style={{ padding: 10, margin: "0 auto" }} src={iconlink} width={100} />
                <div>
                    <h4 style={{ fontWeight: "600" }}>Connect to {app}</h4>
                    <p className="text-muted">
                        You are about to connect your account to <b>{app}</b>.
                    </p>
                </div>
                <Stack gap={2}>
                    <div className="d-flex align-items-center justify-content-center">
                        <CheckIcon fill="green" width={20} height={20} style={{ marginRight: "8px" }} />
                        <span className="text-muted">Secure Connection</span>
                    </div>
                    <div className="d-flex align-items-center justify-content-center">
                        <LockIcon fill="rgb(103, 158, 255)" width={20} height={20} style={{ marginRight: "8px" }} />
                        <span className="text-muted">Your data is protected</span>
                    </div>
                </Stack>
                <p className="text-muted">Redirecting in {countdown} seconds...</p>
                <Button onClick={handleContinue} style={{width:"100%"}}>
                    Continue Now
                </Button>
                <Button variant="outline-secondary" style={{ width: "100%" }}>
                    Deny
                </Button>
            </Stack>
        </div>
    );
};

const AuthPage = () => {
    const { signed, signingIn, login, loadSSO, landingIcon, autologging, setCurrentCompany, deviceVerificationNeeded, setDeviceVerificationNeeded } = useAuth(state => state)
    const [username, setUsername] = useState("")
    const [pass, setPass] = useState("")
    const [passVisible, setPassVisible] = useState(false)
    const [errmsg, setErrmsg] = useState("")
    const { reauth } = useAuth()
    const [serverErr, setServerErr] = useState<AuthData | null>(null)
    const [resignin, setResignin] = useState(false) // resignin from device add
    const navigate = useNavigate()
    const searchParams = useSearchParams()
    const redirectUrl = searchParams.length > 0 && searchParams[0].get("redir")! ? searchParams[0].get("redir")! : "/"
    const client = searchParams.length > 0 ? searchParams[0].get("client")! : ""
    const [inputs, _] = useState([createRef<any>(), createRef<any>()])
    const [authData, setAuthData] = useState<AuthResponse | null>(null)
    const [company, setCompany] = useState<CompanyData | null>(null)
    const isPassport = CLIENT_ID === client || !client
    const { sessionExpired, setSessionExpired } = useAuth(state => state)
    const { loginMfa } = useAuth(state => state)
    const [reauthCred, setReauthCred] = useState({ type: "", value: "" })

    // redirect to sso?client=CLIENT_ID (cpass client id)
    useEffect(() => {
        if (!client) {
            navigate("/sso?client=" + CLIENT_ID)
        }
    }, [client, navigate])

    const onLoginResult = (data: AuthData | null) => {
        if (!data) return

        const msg = data.Msg as AuthResponse
        switch (data.Code) {
            case HTTP_OK:
                const companies = msg.companies
                if (companies.length === 0) {
                    setErrmsg("Internal error.")
                    return
                }
                if (client !== CLIENT_ID) {
                    if (companies.length === 1)
                        setCompany(companies[0])
                    setAuthData(msg)
                }
                else
                    navigate("/manage/me")
                return
            case ERR_SESSION_EXPIRED:
                const msg2 = data.Msg as any;
                setSessionExpired(true, {
                    avatar: msg2.avatar || '',
                    display: msg2.dis || '',
                    isPinEnabled: msg2.is_pin_enabled,
                    isBiometricsEnabled: msg2.is_biometrics_enabled,
                    isPasskeyEnabled: msg2.is_passkey_enabled || false,
                    hasPinSetup: msg2.has_pin_setup || false,
                    hasPasskeySetup: msg2.has_passkey_setup || false
                })
                return;
            case ERR_DEVICE_ADD:
                return;
            case ERR_MFA_REQUIRED:
                // Second-factor challenge is driven by the loginMfa store state / LoginMfaChallenge.
                return;
            case ERR_AWAITING_RESET:
            case ERR_RATE_LIMIT:
            case ERR_ACC_BLOCKED:
            case ERR_DEVICE_APPROVAL_PENDING:
            case ERR_DEVICE_APPROVAL_REJECTED:
            case ERR_IP_NOT_WHITELISTED:
            case USER_NOT_FOUND:
            case ERR_ACCOUNT_FAILED:
            case ERR_INVALID_PIN:
                setErrmsg(getErrorMessage(data.Code, msg));
                return;
            case ERR_AUTH_INVALID:
            case ERR_SESSION_INVALID:
            case ERR_COOKIE:
                return;
            default:
                break;
        }
        console.error("Auth error", data)
        setServerErr(data)
    }
    useEffect(() => {
        if (client) {
            loadSSO(client, redirectUrl)
                .then(res => {
                    // success autologin
                    if (res?.Code === "200") {
                        // Check if device verification is needed is handled in the store now
                        if (!useAuth.getState().deviceVerificationNeeded) {
                            onLoginResult(res)
                        } else {
                            setAuthData(res.Msg as AuthResponse); // Store auth data for later use after verification
                        }
                    } else {
                        onLoginResult(res!)
                    }
                })
                .finally(() => {
                })
        }
    }, [])

    const onCompanySelected = (com: CompanyData) => {
        setErrmsg("")
        setCurrentCompany(com.id, authData!.code)
            .then(res => {
                if (res === "200") {
                    // remember it so the next sign-in defaults to this company
                    setLastCompany(com.id)
                    setCompany(com)
                } else if ([ERR_IP_NOT_WHITELISTED, ERR_DEVICE_APPROVAL_PENDING, ERR_DEVICE_APPROVAL_REJECTED].includes(res)) {
                    setErrmsg(getErrorMessage(res))
                } else {
                    setServerErr({ Code: res } as AuthData)
                }
            })
            .catch(err => {
                setErrmsg(err.message)
            })
    }
    const onReauth = async (credType: string, credValue: string) => {
        const response = await reauth(credValue, credType);
        //console.log(response)
        setReauthCred({ type: credType, value: credValue })
        onLoginResult(response as AuthData)
    }
    const onLogin = async () => {
        setErrmsg("")
        setServerErr(null)
        const res = await login(username, pass, redirectUrl)
        setResignin(false)
        //console.log("LOGIN",res)
        onLoginResult(res)
        if (inputs && inputs.length > 0 && inputs[0].current)
            inputs[0].current.focus()
    }
    const handleVerificationSuccess = () => {
        // Device successfully verified, proceed with login flow
        setDeviceVerificationNeeded(false); // Update the store state
        setResignin(true)
        if (!sessionExpired)
            onLogin()
        else
            onReauth(reauthCred.type, reauthCred.value)
    };

    const handleVerificationError = (error: string) => {
        setErrmsg(error);
    };

    // Check if this is the first login (use localStorage)
    const isFirstAppAccess = (appClient: string) => {
        try {
            // Create unique key per app/client to track first-time access
            const appAccessKey = `cpass_app_access_${appClient}`;
            const hasVisited = localStorage.getItem(appAccessKey);
            return hasVisited === null;
        } catch (e) {
            console.error('localStorage error:', e);
            return true; 
        }
    };

    const markAppAsVisited = (appClient: string) => {
        try {
            const appAccessKey = `cpass_app_access_${appClient}`;
            localStorage.setItem(appAccessKey, 'true');
        } catch (e) {
            console.error('localStorage error:', e);
        }
    };

    const handleContinueToApp = () => {
        // Mark this app as visited
        markAppAsVisited(client);
        redirectToUrl(redirectUrl, authData!.code, company!.id);
    };

    if (serverErr) return <ServerError data={serverErr} redir={redirectUrl} />

    if (resignin || autologging || (landingIcon === null && !isPassport)) return <Loading center />

    // TODO: use able to change user here. current login is different user
    // if the user is already signed and no selected company yet, show company selection
    if (authData !== null && signed && company === null) {
        // preselect the company used last time, when the user still belongs to it
        const lastIndex = authData.companies.findIndex(com => com.id === getLastCompany())
        return <CompanySelect err={errmsg} companies={authData.companies}
            initial={lastIndex < 0 ? 0 : lastIndex}
            onSelect={onCompanySelected} />
    }

    // if the user is already signed and company is selected, show allow page
    // Check if first login - if not, redirect immediately without showing prompt
    if (signed && landingIcon && company !== null) {
        console.log('AllowPage condition met:', { signed, landingIcon, company: company.id, client });
        if (!isFirstAppAccess(client)) {
            // Not first login - redirect immediately
            console.log('Not first login, redirecting...');
            redirectToUrl(redirectUrl, authData!.code, company.id);
            return null;
        }
        console.log('First login, showing prompt...');
        return <AllowPage
            app={authData!.app}
            code={authData!.code}
            redirect={redirectUrl}
            iconlink={landingIcon}
            company={company}
            onContinue={handleContinueToApp}/>
    }

    const onKeyUp = (k: any) => {
        if (k.key !== "Enter") {
            return
        }
        // password focus
        if (username !== "" && pass === "") {
            inputs[0].current.focus()
            setPassVisible(true)
            // hit enter
        } else if (username !== "" && pass !== "") {
            inputs[1].current.click()
        }
    }
    const updateInputs = (_usern: string, _pass: string) => {
        if (_usern !== "") setUsername(_usern)
        if (_pass !== "") setPass(_pass)
        setErrmsg("")
    }
    const onForgot = () => {
        navigate("/forgot")
    }
    // TODO: add remember me function
    const loginDisabled = signingIn || username === "" || pass === ""
    const logo = landingIcon ? landingIcon : Logo
    return <>
        <Stack className="login-box" gap={0}>
            {((!sessionExpired && !loginMfa) || deviceVerificationNeeded) && <Image className="login-img" style={{ alignSelf: "center" }} src={logo} />}
            {sessionExpired && !deviceVerificationNeeded && <Reauth error={errmsg} onConfirm={onReauth} />}

            {loginMfa && !sessionExpired && !deviceVerificationNeeded && <LoginMfaChallenge error={errmsg} onResult={onLoginResult} />}

            {deviceVerificationNeeded && <DeviceVerification
                onVerificationSuccess={handleVerificationSuccess}
                onVerificationError={handleVerificationError}
            />}
            {!sessionExpired && !loginMfa && <>
                {!deviceVerificationNeeded && <Label size="22px" textAlign="center">CPASS Login</Label>}
                {!deviceVerificationNeeded && <Form.Control style={{ marginTop: "20px" }}
                    onKeyUp={onKeyUp}
                    autoFocus
                    disabled={signingIn}
                    placeholder="Username"
                    onChange={(e) => updateInputs(e.target.value, "")} />}
                {/* <Fade in={passVisible}> */}
                {!deviceVerificationNeeded && <Form.Control style={{ marginTop: "5px" }}
                    onKeyUp={onKeyUp}
                    ref={inputs[0]}
                    disabled={signingIn}
                    placeholder="Password"
                    type="password"
                    onChange={(e) => updateInputs("", e.target.value)} />}
                {/* </Fade> */}
                {/* Display error message */}
                {!deviceVerificationNeeded && errmsg !== "" && (
                    <Form.Label className="login-err">
                        {errmsg}
                    </Form.Label>
                )}
                {!deviceVerificationNeeded && <Fade in={pass !== "" && username !== ""}>
                    <Button ref={inputs[1]} className="login-button" disabled={loginDisabled} onClick={onLogin}>
                        {
                            signingIn ? <Loading /> : "Login"
                        }
                    </Button></Fade>}
                {!deviceVerificationNeeded && username !== "" && <Button className="login-forgot" variant="outline" onClick={onForgot}>
                    <Label color="#006eff" size="medium" fontWeight="400" textAlign="center">Forgot password?</Label>
                </Button>}
            </>}
        </Stack>
    </>
}

export default AuthPage;
